import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
// import { CssBaseline } from '@mui/material';

// Theme Context
const ThemeContext = createContext();

// Custom hook to use theme context
export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

// Color palette based on your SCSS variables
const colors = {
  primary: '#62b03b',
  primaryDark: '#083d1a',
  secondary: '#1c4070',
  secondaryDark: '#0d336b',
  error: '#d32f2f',
  warning: '#ed6c02',
  info: '#0288d1',
  success: '#558c63',
  danger: '#bc4040',
  light: '#f7f7f7',
  grey: '#606c76',
  greyBg: '#f8f8f8',
  black: '#202020',
  white: '#fdfdfd',
  disabled: '#606c76',
};

// Typography configuration based on your SCSS - adjusted for 10px rem base
const typography = {
  fontFamily: "'Roboto', Arial, sans-serif",
  htmlFontSize: 10, // Tell MUI that 1rem = 10px in your setup
  h1: {
    fontSize: '3rem', // 30px
    fontWeight: 'bold',
    letterSpacing: '0.01em',
  },
  h2: {
    fontSize: '2.5rem', // 25px
    fontWeight: 'bold',
    letterSpacing: '0.01em',
  },
  h3: {
    fontSize: '2rem', // 20px
    fontWeight: 'bold',
    letterSpacing: '0.01em',
  },
  h4: {
    fontSize: '1.5rem', // 15px
    fontWeight: 'bold',
    letterSpacing: '0.01em',
  },
  h5: {
    fontSize: '1.25rem', // 12.5px
    fontWeight: 'bold',
    letterSpacing: '0.01em',
  },
  h6: {
    fontSize: '1.1rem', // 11px
    fontWeight: 'bold',
    letterSpacing: '0.01em',
  },
  body1: {
    fontSize: '1.6rem', // 16px - matches Milligram default
    letterSpacing: '0.01em',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '1.4rem', // 14px
    letterSpacing: '0.01em',
    lineHeight: 1.6,
  },
  button: {
    fontSize: '1.2rem', // 12px
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: '0.15rem',
  },
  caption: {
    fontSize: '1rem', // 10px
    letterSpacing: '0.01em',
  },
  overline: {
    fontSize: '1rem', // 10px
    textTransform: 'uppercase',
    letterSpacing: '0.15rem',
  },
};


// Spacing configuration - adjusted for Milligram's 62.5% base (1rem = 10px)
const spacing = (factor) => `${factor}rem`;

// Create theme function
const createAppTheme = (mode) => {
  const isLight = mode === 'light';
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        dark: colors.primaryDark,
        light: isLight ? '#7cc355' : '#4a8c2a',
        contrastText: colors.white,
      },
      secondary: {
        main: colors.secondary,
        dark: colors.secondaryDark,
        light: isLight ? '#4a5d91' : '#152e5c',
        contrastText: colors.white,
      },
      error: {
        main: colors.error,
        light: isLight ? '#e57373' : '#b71c1c',
        dark: '#c62828',
        contrastText: colors.white,
      },
      warning: {
        main: colors.warning,
        light: isLight ? '#ff9800' : '#e65100',
        dark: '#e65100',
        contrastText: colors.white,
      },
      info: {
        main: colors.info,
        light: isLight ? '#29b6f6' : '#01579b',
        dark: '#01579b',
        contrastText: colors.white,
      },
      success: {
        main: colors.success,
        light: isLight ? '#81c784' : '#2e7d32',
        dark: '#388e3c',
        contrastText: colors.white,
      },
      grey: {
        50: isLight ? colors.light : '#303030',
        100: isLight ? colors.greyBg : '#424242',
        200: isLight ? '#e0e0e0' : '#616161',
        300: isLight ? '#bdbdbd' : '#757575',
        400: isLight ? '#9e9e9e' : '#9e9e9e',
        500: colors.grey,
        600: isLight ? '#757575' : '#e0e0e0',
        700: isLight ? '#616161' : '#f5f5f5',
        800: isLight ? '#424242' : colors.greyBg,
        900: isLight ? colors.black : colors.light,
      },
      background: {
        default: isLight ? colors.light : '#121212',
        paper: isLight ? colors.white : '#1e1e1e',
        secondary: isLight ? colors.greyBg : '#2a2a2a',
      },
      text: {
        primary: isLight ? colors.black : colors.white,
        secondary: isLight ? colors.grey : '#b0b0b0',
        disabled: colors.disabled,
      },
      action: {
        active: isLight ? colors.secondary : colors.primary,
        hover: isLight ? 'rgba(28, 64, 112, 0.04)' : 'rgba(98, 176, 59, 0.08)',
        selected: isLight ? 'rgba(28, 64, 112, 0.08)' : 'rgba(98, 176, 59, 0.12)',
        disabled: colors.disabled,
        disabledBackground: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
      },
      divider: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
    },
    typography,
    spacing,
    shape: {
      borderRadius: 4,
    }
  });
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const urlTheme = urlParams.get('theme');
    if (urlTheme === 'dark' || urlTheme === 'light') {
      return urlTheme;
    }
    // Get initial mode from localStorage or default to light
    const savedMode = localStorage.getItem('themeMode') || sessionStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  const theme = createAppTheme(mode);

  // Apply theme attribute to document element
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const toggleColorMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const setColorMode = (newMode) => {
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  /*
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      const savedMode = localStorage.getItem('themeMode');
      if (!savedMode) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    
    // Set initial mode if no saved preference
    if (!localStorage.getItem('themeMode')) {
      setMode(mediaQuery.matches ? 'dark' : 'light');
    }

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  */

  const contextValue = {
    mode,
    toggleColorMode,
    setColorMode,
    theme,
    colors,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider; 